import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { homeApi, type HomeDrillResponse, type HomeResponse } from '../api/home'
import { getFeatureFlags } from '../lib/featureFlags'
import { Button, Callout, Card, CardContent, CardHeader, EmptyState, LoadingState, Page, PageHeader, StatCard, StatGrid } from '../components/ui'

const PROFILE_TITLE: Record<HomeResponse['profile'], string> = {
  agente: 'Il mio lavoro',
  backoffice: 'Backoffice',
  amministrazione: 'Amministrazione',
  direzione: 'Direzione',
}

const eur = (v: number) => '€ ' + v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Atterraggio: con il flag `home.per_ruolo` acceso porta alla home per ruolo, altrimenti alla home di sempre. */
export function HomeLanding({ fallback }: { fallback: string }) {
  const [target, setTarget] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    // ADR-019: con Equal Point acceso si entra dalla scelta del ramo.
    void getFeatureFlags().then(f => {
      if (alive) setTarget(f['equalpoint.rami'] === true ? '/equal-point' : f['home.per_ruolo'] === true ? '/home' : fallback)
    })
    return () => { alive = false }
  }, [fallback])
  return target ? <Navigate to={target} replace /> : <LoadingState />
}

/** C-15 - pagina iniziale per ruolo: le anomalie in cima, ogni numero apre l'elenco con lo stesso totale. */
export function RoleHomePage() {
  const [home, setHome] = useState<HomeResponse | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    homeApi.get()
      .then(h => { if (alive) setHome(h) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Errore') })
    return () => { alive = false }
  }, [])

  if (error) return <Page><Callout tone="error">{error}</Callout></Page>
  if (!home) return <Page><LoadingState /></Page>

  return (
    <Page>
      <PageHeader title="Home" subtitle={PROFILE_TITLE[home.profile]} />
      {home.sections.length === 0 && <EmptyState icon="inbox" title="Niente da mostrare" description="Non ci sono numeri per il tuo ruolo." />}
      {home.sections.map(section => (
        <Card key={section.key} className="mb-3">
          <CardHeader title={section.title} />
          <CardContent>
            <StatGrid>
              {section.tiles.map(t => (
                <Link key={t.key} to={`/home/${t.key}`} className="text-decoration-none d-block h-100">
                  <StatCard
                    className="h-100"
                    label={t.label}
                    value={t.amount != null ? eur(t.amount) : String(t.value)}
                    supporting={t.amount != null ? `${t.hint} (${t.value} righe)` : t.hint}
                    tone={t.anomaly && t.value > 0 ? 'error' : 'primary'}
                  />
                </Link>
              ))}
            </StatGrid>
          </CardContent>
        </Card>
      ))}
    </Page>
  )
}

/** Drill-down di un numero della home: l'elenco che lo compone (stesso totale). */
export function HomeTilePage() {
  const { key = '' } = useParams()
  const [data, setData] = useState<HomeDrillResponse | null>(null)
  const [page, setPage] = useState(1)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    homeApi.tile(key, page)
      .then(d => { if (alive) setData(d) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Errore') })
    return () => { alive = false }
  }, [key, page])

  if (error) return <Page><Callout tone="error">{error}</Callout></Page>
  if (!data) return <Page><LoadingState /></Page>

  const pages = Math.max(1, Math.ceil(data.total / data.pageSize))
  return (
    <Page>
      <PageHeader title={data.label} subtitle={data.amount != null ? `${data.total} in totale - ${eur(data.amount)}` : `${data.total} in totale`} />
      <Card>
        <CardContent>
          {data.items.length === 0 ? (
            <EmptyState icon="inbox" title="Niente in coda" description="Nessuna riga per questo numero." />
          ) : (
            <ul className="list-unstyled mb-0">
              {data.items.map(i => (
                <li key={i.id} className="py-1"><Link to={i.route}>{i.title}</Link>{i.amount != null ? <span className="ms-2 text-muted">{eur(i.amount)}</span> : null}</li>
              ))}
            </ul>
          )}
          <div className="d-flex gap-2 mt-3">
            <Button variant="outlined" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Precedente</Button>
            <Button variant="outlined" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Successiva</Button>
            <Link to={data.route} className="ms-auto">Apri la pagina completa</Link>
            <Link to="/home">Torna alla home</Link>
          </div>
        </CardContent>
      </Card>
    </Page>
  )
}
